import axios, { AxiosError } from 'axios';
import { UserRole } from '../types/auth';

/**
 * User Service
 *
 * Handles all user-related API requests to the backend.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// User endpoints
const USER_ENDPOINTS = {
  list: `${API_BASE_URL}/users`,
  byId: (id: string) => `${API_BASE_URL}/users/${id}`,
  update: (id: string) => `${API_BASE_URL}/users/${id}`,
  deactivate: (id: string) => `${API_BASE_URL}/users/${id}`,
  resendVerification: (id: string) => `${API_BASE_URL}/users/${id}/resend-verification`,
  resetPassword: (id: string) => `${API_BASE_URL}/users/${id}/reset-password`,
  invite: `${API_BASE_URL}/users/invite`,
  dependencies: (id: string) => `${API_BASE_URL}/users/${id}/dependencies`,
  permanentDelete: (id: string) => `${API_BASE_URL}/users/${id}/permanent`,
  accessLimits: (id: string) => `${API_BASE_URL}/users/${id}/access-limits`
};

/**
 * User interface matching backend
 */
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  company_id: string;
  company_name?: string;
  /** Set only for role='customer' users — the Customer entity they're scoped to. */
  customer_id?: string | null;
  email_verified: boolean;
  status: 'active' | 'inactive' | 'pending';
  last_login?: string | null;
  created_at: string;
  updated_at?: string | null;
}

/**
 * User creation input
 */
export interface CreateUserInput {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  role?: UserRole;
  company_id?: string;
}

/**
 * User update input
 */
export interface UpdateUserInput {
  first_name?: string;
  last_name?: string;
  role?: UserRole;
  status?: 'active' | 'inactive' | 'pending';
  /** "Limit Access to Only" — team ids whose projects (by owner) this user may see. */
  limit_team_ids?: string[];
  /** "Limit Access to Only" — explicit project ids this user may see. */
  limit_project_ids?: string[];
}

/** A user's saved "Limit Access to Only" selections. */
export interface UserAccessLimits {
  team_ids: string[];
  project_ids: string[];
}

/**
 * Pagination metadata
 */
export interface PaginationMetadata {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * List users query parameters
 */
export interface ListUsersParams {
  page?: number;
  limit?: number;
  status?: 'active' | 'inactive' | 'pending';
  role?: UserRole;
  search?: string;
  company_id?: string;
  customer_id?: string;
}

/**
 * API Response interfaces
 */
export interface UsersListResponse {
  success: boolean;
  data: {
    users: User[];
    pagination: PaginationMetadata;
  };
}

export interface UserResponse {
  success: boolean;
  data: User;
}

export interface MessageResponse {
  success: boolean;
  message: string;
}

export interface APIError {
  error: string;
  details?: string;
}

/**
 * Handle API errors
 */
function handleAPIError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIError>;
    const errorMessage = axiosError.response?.data?.error || axiosError.message;
    throw new Error(errorMessage);
  }
  throw error;
}

/**
 * Create a new user
 */
export async function createUser(
  data: CreateUserInput,
  token: string
): Promise<User> {
  try {
    const response = await axios.post<UserResponse>(
      USER_ENDPOINTS.list,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * List users with pagination and filtering
 */
export async function listUsers(
  params: ListUsersParams,
  token: string
): Promise<{ users: User[]; pagination: PaginationMetadata }> {
  try {
    const response = await axios.get<UsersListResponse>(USER_ENDPOINTS.list, {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get user by ID
 */
export async function getUserById(id: string, token: string): Promise<User> {
  try {
    const response = await axios.get<UserResponse>(USER_ENDPOINTS.byId(id), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Update user
 */
export async function updateUser(
  id: string,
  data: UpdateUserInput,
  token: string
): Promise<User> {
  try {
    const response = await axios.put<UserResponse>(
      USER_ENDPOINTS.update(id),
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get a user's "Limit Access to Only" selections (team + project ids).
 */
export async function getUserAccessLimits(id: string, token: string): Promise<UserAccessLimits> {
  try {
    const response = await axios.get<{ success: boolean; data: UserAccessLimits }>(
      USER_ENDPOINTS.accessLimits(id),
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Deactivate user
 */
export async function deactivateUser(id: string, token: string): Promise<void> {
  try {
    await axios.delete<MessageResponse>(USER_ENDPOINTS.deactivate(id), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Resend verification email
 */
export async function resendVerification(id: string, token: string): Promise<void> {
  try {
    await axios.post<MessageResponse>(
      USER_ENDPOINTS.resendVerification(id),
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Reset user password (admin only)
 */
export async function resetUserPassword(
  id: string,
  newPassword: string,
  token: string
): Promise<void> {
  try {
    await axios.post<MessageResponse>(
      USER_ENDPOINTS.resetPassword(id),
      { new_password: newPassword },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Bulk invite users
 */
export interface InviteBulkInput {
  email: string;
  role: 'project_manager' | 'project_manager_lite' | 'contributor';
}

export interface InviteBulkResponse {
  success: boolean;
  data: {
    sent: number;
    failed: number;
    results: Array<{
      email: string;
      status: 'sent' | 'failed';
      user_id?: string;
      error?: string;
    }>;
  };
}

export async function inviteBulk(
  invitations: InviteBulkInput[],
  token: string
): Promise<InviteBulkResponse['data']> {
  try {
    const response = await axios.post<InviteBulkResponse>(
      `${API_BASE_URL}/users/invite-bulk`,
      { invitations },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Check if email exists
 */
export interface CheckEmailResponse {
  success: boolean;
  exists: boolean;
  in_company: boolean;
}

export async function checkEmailExists(email: string, token: string): Promise<{ exists: boolean; in_company: boolean }> {
  try {
    const response = await axios.get<CheckEmailResponse>(
      `${API_BASE_URL}/users/check-email`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: { email }
      }
    );
    return { exists: response.data.exists, in_company: response.data.in_company };
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * User preferences interface
 */
export interface UserPreferences {
  [key: string]: any; // Flexible preferences object
}

export interface UserPreferencesResponse {
  success: boolean;
  data: UserPreferences;
}

/**
 * Get current user's UI preferences
 */
export async function getUserPreferences(token: string): Promise<UserPreferences> {
  try {
    const response = await axios.get<UserPreferencesResponse>(
      `${API_BASE_URL}/users/preferences`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Update current user's UI preferences
 */
export async function updateUserPreferences(
  preferences: UserPreferences,
  token: string
): Promise<UserPreferences> {
  try {
    const response = await axios.put<UserPreferencesResponse>(
      `${API_BASE_URL}/users/preferences`,
      { preferences },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Invite a single user to the company via email
 */
export async function inviteUser(
  data: { email: string; role: string },
  token: string
): Promise<{ id: string; email: string; role: string; status: string }> {
  try {
    const response = await axios.post<{ success: boolean; message: string; data: { id: string; email: string; role: string; status: string } }>(
      USER_ENDPOINTS.invite,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * User dependencies response (for permanent delete)
 */
export interface UserDependencies {
  business: {
    projects_owned: number;
    projects_created: number;
    project_memberships: number;
    requirements_created: number;
    requirements_assigned: number;
    sprints_created: number;
    time_logs: number;
  };
  has_dependencies: boolean;
}

export interface UserDependenciesResponse {
  success: boolean;
  data: {
    user: {
      id: string;
      email: string;
      first_name: string;
      last_name: string;
      role: string;
    };
    dependencies: UserDependencies;
  };
}

/**
 * Get FK dependency counts for a user (before permanent deletion)
 */
export async function getUserDependencies(
  id: string,
  token: string
): Promise<UserDependenciesResponse['data']> {
  try {
    const response = await axios.get<UserDependenciesResponse>(
      USER_ENDPOINTS.dependencies(id),
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Permanently delete a user (reassigns all data to another user first)
 */
export async function permanentDeleteUser(
  id: string,
  reassignTo: string,
  token: string
): Promise<void> {
  try {
    await axios.delete<MessageResponse>(
      USER_ENDPOINTS.permanentDelete(id),
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: { reassign_to: reassignTo }
      }
    );
  } catch (error) {
    handleAPIError(error);
  }
}

export default {
  createUser,
  listUsers,
  getUserById,
  updateUser,
  deactivateUser,
  resendVerification,
  resetUserPassword,
  inviteBulk,
  inviteUser,
  checkEmailExists,
  getUserPreferences,
  updateUserPreferences,
  getUserDependencies,
  permanentDeleteUser
};
